<template>
  <div class="grid" :class="gridClass">
    <div
      v-for="(photo, index) in photos"
      :key="index"
      class="photo"
      :class="`photo--${getPhotoClass(index, layout)}`"
      :data-photo-index="index"
      @click="$emit('photoClick', index)"
    >
      <SkeletonItem v-if="shouldShowSkeleton" class="skeleton" border-radius="0px" />
      <img
        v-else
        :src="getPreviewImageSrc(photo, 'desktop')"
        :alt="t('photoAlt', { index: index + 1 })"
        class="image"
        :width="getPreviewImageWidth(photo, 'desktop')"
        :height="getPreviewImageHeight(photo, 'desktop')"
        :loading="getPreviewImageLoading(index)"
        :fetchpriority="getPreviewImageFetchPriority(index)"
        decoding="async"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { SkeletonItem } from '@experience/core-skeleton-lib';
import { useI18n } from '@experience/i18n';
import { computed } from 'vue';

import ru from '../i18n/ru.json';
import { getLayoutSlice } from '../lib/get-displayed-photos';
import { getPhotoClass } from '../lib/get-photo-class';
import {
  getPreviewImageFetchPriority,
  getPreviewImageHeight,
  getPreviewImageLoading,
  getPreviewImageSrc,
  getPreviewImageWidth,
} from '../lib/preview-image';

import type { ExperiencePhoto } from '../types/experience';
import type { GalleryLayout } from '../types/gallery-layout';

interface Props {
  photos: ExperiencePhoto[];
  layout: GalleryLayout;
}

const props = defineProps<Props>();

defineEmits<{
  photoClick: [index: number];
}>();

const { t } = useI18n<{ message: typeof ru }, 'ru'>({
  inheritLocale: true,
  messages: { ru },
});

const shouldShowSkeleton = computed(() => !props.photos.length);

// раскладки рассчитаны на 4–6 фото и оставляют пустые колонки, если снимков меньше,
// поэтому для неполного набора берём раскладку по их количеству
const gridClass = computed(() => {
  const count = props.photos.length;

  return count && count < getLayoutSlice(props.layout) ? `grid--count-${count}` : `grid--${props.layout}`;
});
</script>

<style scoped lang="scss">
.grid {
  display: grid;
  grid-template-rows: repeat(2, 1fr);
  grid-template-columns: repeat(10, 1fr);
  gap: var(--alias-space-4);
  height: 480px;

  @include tablet {
    height: 360px;
  }

  @include mobile {
    display: none;
  }
}

// раскладки для неполного набора: позиционируем по индексу, ориентация фото здесь не важна
.grid--count-1 {
  grid-template-columns: 1fr;

  .photo {
    grid-row: 1 / 3;
    grid-column: 1;
  }
}

.grid--count-2 {
  .photo[data-photo-index='0'] {
    grid-row: 1 / 3;
    grid-column: 1 / 6;
  }

  .photo[data-photo-index='1'] {
    grid-row: 1 / 3;
    grid-column: 6 / 11;
  }
}

.grid--count-3 {
  .photo[data-photo-index='0'] {
    grid-row: 1 / 3;
    grid-column: 1 / 7;
  }

  .photo[data-photo-index='1'] {
    grid-row: 1;
    grid-column: 7 / 11;
  }

  .photo[data-photo-index='2'] {
    grid-row: 2;
    grid-column: 7 / 11;
  }
}

.grid--count-4 {
  .photo[data-photo-index='0'] {
    grid-row: 1 / 3;
    grid-column: 1 / 5;
  }

  .photo[data-photo-index='1'] {
    grid-row: 1;
    grid-column: 5 / 8;
  }

  .photo[data-photo-index='2'] {
    grid-row: 2;
    grid-column: 5 / 8;
  }

  .photo[data-photo-index='3'] {
    grid-row: 1 / 3;
    grid-column: 8 / 11;
  }
}

.grid--count-5 {
  .photo[data-photo-index='0'] {
    grid-row: 1 / 3;
    grid-column: 1 / 7;
  }

  .photo[data-photo-index='1'] {
    grid-row: 1;
    grid-column: 7 / 9;
  }

  .photo[data-photo-index='2'] {
    grid-row: 2;
    grid-column: 7 / 9;
  }

  .photo[data-photo-index='3'] {
    grid-row: 1;
    grid-column: 9 / 11;
  }

  .photo[data-photo-index='4'] {
    grid-row: 2;
    grid-column: 9 / 11;
  }
}

.grid--vertical-horizontal {
  .photo--vertical {
    grid-row: 1 / 3;
    grid-column: 1 / 4;
  }

  .photo--horizontal {
    grid-row: 1 / 3;
    grid-column: 4 / 9;
  }

  .photo--square[data-photo-index='2'] {
    grid-row: 1;
    grid-column: 9 / 11;
  }

  .photo--square[data-photo-index='3'] {
    grid-row: 2;
    grid-column: 9 / 11;
  }
}

.grid--horizontal-horizontal {
  .photo--horizontal[data-photo-index='0'] {
    grid-row: 1 / 3;
    grid-column: 1 / 7;
  }

  .photo--square[data-photo-index='1'] {
    grid-row: 1;
    grid-column: 7 / 9;
  }

  .photo--square[data-photo-index='2'] {
    grid-row: 2;
    grid-column: 7 / 9;
  }

  .photo--square[data-photo-index='3'] {
    grid-row: 1;
    grid-column: 9 / 11;
  }

  .photo--square[data-photo-index='4'] {
    grid-row: 2;
    grid-column: 9 / 11;
  }
}

.grid--vertical-vertical {
  .photo--vertical[data-photo-index='0'] {
    grid-row: 1 / 3;
    grid-column: 1 / 4;
  }

  .photo--vertical[data-photo-index='1'] {
    grid-row: 1 / 3;
    grid-column: 4 / 7;
  }

  .photo--square[data-photo-index='2'] {
    grid-row: 1;
    grid-column: 7 / 9;
  }

  .photo--square[data-photo-index='3'] {
    grid-row: 1;
    grid-column: 9 / 11;
  }

  .photo--square[data-photo-index='4'] {
    grid-row: 2;
    grid-column: 7 / 9;
  }

  .photo--square[data-photo-index='5'] {
    grid-row: 2;
    grid-column: 9 / 11;
  }
}

.grid--square-square {
  .photo--crop[data-photo-index='0'] {
    grid-row: 1 / 3;
    grid-column: 1 / 5;
  }

  .photo--crop[data-photo-index='1'] {
    grid-row: 1 / 3;
    grid-column: 5 / 9;
  }

  .photo--square[data-photo-index='2'] {
    grid-row: 1;
    grid-column: 9 / 11;
  }

  .photo--square[data-photo-index='3'] {
    grid-row: 2;
    grid-column: 9 / 11;
  }
}

.photo {
  position: relative;
  overflow: hidden;
  cursor: pointer;
  background: var(--alias-color-skeleton-default);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.skeleton {
  width: 100%;
  height: 100%;
}
</style>
